import React, { useState, useEffect } from "react";
import api from "../utils/api";
import { toast } from "react-toastify";
import {
  FaExchangeAlt,
  FaRocket,
  FaUsers,
  FaTrophy,
  FaArrowDown,
  FaChevronLeft,
  FaChevronRight,
  FaFilter,
  FaReceipt,
} from "react-icons/fa";

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const fetchTransactions = async () => {
    try {
      const response = await api.get(
        `/users/transactions?page=${page}&limit=20`,
      );
      setTransactions(response.data.transactions);
      setTotalPages(response.data.pages);
    } catch (error) {
      toast.error("Failed to load transactions");
    } finally {
      setLoading(false);
    }
  };

  const getTransactionIcon = (type) => {
    const icons = {
      activation: FaRocket,
      referral_level1: FaUsers,
      referral_level2: FaUsers,
      referral_level3: FaUsers,
      stage_bonus: FaTrophy,
      withdrawal: FaArrowDown,
    };
    return icons[type] || FaExchangeAlt;
  };

  const getTransactionStyle = (type) => {
    const styles = {
      activation: {
        text: "text-cyan-400",
        bg: "bg-cyan-500/10",
        border: "border-cyan-500/20",
      },
      referral_level1: {
        text: "text-emerald-400",
        bg: "bg-emerald-500/10",
        border: "border-emerald-500/20",
      },
      referral_level2: {
        text: "text-emerald-400",
        bg: "bg-emerald-500/10",
        border: "border-emerald-500/20",
      },
      referral_level3: {
        text: "text-emerald-400",
        bg: "bg-emerald-500/10",
        border: "border-emerald-500/20",
      },
      stage_bonus: {
        text: "text-violet-400",
        bg: "bg-violet-500/10",
        border: "border-violet-500/20",
      },
      withdrawal: {
        text: "text-rose-400",
        bg: "bg-rose-500/10",
        border: "border-rose-500/20",
      },
    };
    return (
      styles[type] || {
        text: "text-slate-400",
        bg: "bg-white/[0.04]",
        border: "border-white/10",
      }
    );
  };

  const filteredTransactions =
    filter === "all" ? transactions : (
      transactions.filter((t) => t.type.includes(filter))
    );

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-[60vh] gap-3">
        <div className="w-12 h-12 rounded-full border-[3px] border-cyan-400/30 border-t-cyan-400 animate-spin" />
        <p className="text-sm text-slate-400 animate-pulse">Loading...</p>
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-6">
      {/* Hero Card */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-sky-500 via-blue-600 to-indigo-600 p-5 shadow-xl shadow-blue-500/10">
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-white/10 blur-xl" />
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <FaReceipt className="text-lg text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Transactions</h1>
              <p className="text-[11px] text-blue-100/80 font-medium">
                All earnings & activity
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Pills */}
      <div className="relative">
        <div className="absolute top-0 right-0 bottom-1 w-8 bg-gradient-to-l from-slate-950 to-transparent z-10 pointer-events-none" />
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {[
            { id: "all", label: "All", icon: FaFilter },
            { id: "referral", label: "Referrals", icon: FaUsers },
            { id: "stage", label: "Stages", icon: FaTrophy },
            { id: "activation", label: "Activation", icon: FaRocket },
          ].map((f) => {
            const Icon = f.icon;
            return (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all duration-200 touch-manipulation ${
                  filter === f.id ?
                    "bg-white/10 text-white border border-white/20 shadow-sm"
                  : "bg-white/[0.04] text-slate-500 border border-white/[0.06]"
                }`}
              >
                <Icon className="text-[10px]" />
                {f.label}
              </button>
            );
          })}
          <div className="flex-shrink-0 w-4" />
        </div>
      </div>

      {/* Transactions List */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4">
        {filteredTransactions.length === 0 ?
          <div className="text-center py-10">
            <div className="w-14 h-14 rounded-full bg-white/[0.05] flex items-center justify-center mx-auto mb-3 border border-white/[0.08]">
              <FaExchangeAlt className="text-xl text-slate-500" />
            </div>
            <p className="text-sm font-medium text-slate-400">
              No transactions found
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Your activity will appear here
            </p>
          </div>
        : <div className="space-y-2">
            {filteredTransactions.map((transaction) => {
              const Icon = getTransactionIcon(transaction.type);
              const style = getTransactionStyle(transaction.type);
              return (
                <div
                  key={transaction._id}
                  className="flex items-center gap-3 px-3.5 py-3 rounded-xl bg-white/[0.02] border border-white/[0.05] active:bg-white/[0.05] transition-colors"
                >
                  <div
                    className={`w-9 h-9 rounded-xl ${style.bg} border ${style.border} flex items-center justify-center flex-shrink-0`}
                  >
                    <Icon className={`text-xs ${style.text}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">
                      {transaction.description}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[11px] text-slate-500">
                        {new Date(transaction.createdAt).toLocaleDateString(
                          undefined,
                          { month: "short", day: "numeric" },
                        )}
                      </span>
                      {transaction.relatedUser && (
                        <>
                          <span className="w-0.5 h-0.5 bg-slate-600 rounded-full" />
                          <span className="text-[11px] text-slate-500 truncate">
                            @{transaction.relatedUser.username}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`text-sm font-bold ${style.text}`}>
                      {transaction.type === "withdrawal" ? "-" : "+"}$
                      {transaction.amount.toFixed(2)}
                    </p>
                    <span
                      className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border capitalize ${
                        transaction.status === "completed" ?
                          "text-emerald-300 bg-emerald-500/15 border-emerald-500/30"
                        : transaction.status === "pending" ?
                          "text-amber-300 bg-amber-500/15 border-amber-500/30"
                        : "text-rose-300 bg-rose-500/15 border-rose-500/30"
                      }`}
                    >
                      {transaction.status}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        }

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-4 pt-3 border-t border-white/[0.06]">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="w-9 h-9 rounded-xl bg-white/[0.06] border border-white/10 flex items-center justify-center text-slate-400 disabled:opacity-30 disabled:cursor-not-allowed active:scale-[0.95] transition-transform touch-manipulation"
            >
              <FaChevronLeft className="text-xs" />
            </button>
            <span className="text-xs text-slate-400 font-medium">
              {page} <span className="text-slate-600">of</span> {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="w-9 h-9 rounded-xl bg-white/[0.06] border border-white/10 flex items-center justify-center text-slate-400 disabled:opacity-30 disabled:cursor-not-allowed active:scale-[0.95] transition-transform touch-manipulation"
            >
              <FaChevronRight className="text-xs" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Transactions;
